// frontend/src/lib/forecastService.js
/**
 * Single source for forecast values consumed by the UI.
 *
 * Primary: GET /api/forecast/active — the latest Drive-synced snapshot.
 * Fallback: the static forecast2026-2031.js (canonical CAD-negative) — used
 * only if the API call fails, so the page never goes blank during rollout.
 *
 * Returns the canonical shape:
 *   { currency: 'CAD', byYear: { '2026': { depts: { <dept>: { monthly_total: [...], by_gl: {...} } } } } }
 */
import { forecastPayload2026_2031 } from '../data/forecast2026-2031';
import { queuedFetch } from './fetchQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function staticFallback() {
  // The XLSX/CSV-generated payload is already in canonical CAD-signed-negative shape.
  return { ...forecastPayload2026_2031, source: 'static-import', meta: null };
}

export async function fetchActiveForecast({ companyId }) {
  try {
    const r = await queuedFetch(
      `${BACKEND_URL}/api/forecast/active?company_id=${encodeURIComponent(companyId)}`
    );
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const row = await r.json();
    return {
      currency: row.payload.currency,
      byYear: row.payload.byYear,
      meta: {
        snapshot_id: row.id,
        synced_at: row.synced_at,
        modified_by_email: row.modified_by_email,
        modified_at: row.modified_at,
        rows_count: row.rows_count,
        sheet_tab: row.sheet_tab,
        warnings: row.parse_warnings || [],
      },
      source: 'drive-snapshot',
    };
  } catch (e) {
    console.warn('Forecast API failed, using static forecast2026-2031.js:', e);
    return staticFallback();
  }
}

export function lookupForecastCAD({ forecast, dept, ym, fxRate }) {
  if (!forecast || !ym) return null;
  const [yStr, mStr] = ym.split('-');
  const arr = forecast.byYear?.[yStr]?.depts?.[dept]?.monthly_total;
  if (!arr) return null;
  const raw = arr[parseInt(mStr, 10) - 1];
  if (raw == null) return null;
  // Canonical payload from Drive is CAD already (negative).
  // Legacy payload is USD positive → convert to negative CAD via fxRate.
  if (forecast.currency === 'CAD') return raw;
  return -Math.round(raw * (fxRate || 1.37));
}

/**
 * Returns { [gl]: { name, forecastCAD } } for a given dept × month.
 * Only the Drive-synced payload has GL-level detail; legacy fallback returns {}.
 */
export function lookupForecastByGL({ forecast, dept, ym, fxRate }) {
  if (!forecast || !ym) return {};
  const [yStr, mStr] = ym.split('-');
  const monthIdx = parseInt(mStr, 10) - 1;
  const byGl = forecast.byYear?.[yStr]?.depts?.[dept]?.by_gl;
  if (!byGl) return {};
  const out = {};
  for (const [gl, def] of Object.entries(byGl)) {
    const raw = def?.monthly?.[monthIdx];
    if (raw == null) continue;
    const cad = forecast.currency === 'CAD' ? raw : -Math.round(raw * (fxRate || 1.37));
    out[gl] = { name: def.name || '', forecastCAD: cad };
  }
  return out;
}
